import TimeSignature from 'Models/TimeSignature';
import { noteFromOsmdPitch, midiNumberToBaseNote } from 'Phaser/Util/OSMDHelper';
import { PrerenderedPhraseData } from 'Types';
import type { BaseData, ClefData, MeasureProps, CursorData, VoiceData, Config } from './Types';
import { GameObjects, Scene } from 'phaser';
import NoteGraphic from './Note';
import RestGraphic from './Rest';
import Clef, { getNoteLineNumber } from './Clef';
import ClefMeasure, { MeasureDataExtra } from './Measure';
import { addOptional } from 'Phaser/util';
import { getColorByCbType } from 'Components/Colors';
import { COLORBLIND_TYPE, ERROR_REC_CLASS } from 'Utils/Constants';
import { MidiEventState } from 'Types';
import { Logger } from 'oidc-client-ts';
import { PhraseDimens } from './Util';

type PhraseGraphicResizeParams = {
  newConfig: Config;
  width: number;
  x: number;
  y: number;
  phraseDimens: PhraseDimens;
  phraseHeightInner: number;
};

function configValuesEqual(a: Config, b: Config): boolean {
  return (
    a.globalScaling === b.globalScaling &&
    a.noteScaling === b.noteScaling &&
    a.pitchLineWidth === b.pitchLineWidth &&
    a.pitchLineGap === b.pitchLineGap &&
    a.measureLineWidth === b.measureLineWidth &&
    a.clefGap === b.clefGap &&
    a.firstNoteGap === b.firstNoteGap &&
    a.clefSymbolMargin === b.clefSymbolMargin &&
    a.centreAlign === b.centreAlign &&
    a.notePaddingFactor === b.notePaddingFactor &&
    a.doubleEndLines === b.doubleEndLines
  );
}

function phraseDimensValuesEqual(a: PhraseDimens, b: PhraseDimens): boolean {
  return (
    a.bbHeight === b.bbHeight &&
    a.innerHeight === b.innerHeight &&
    a.topClefHeight.innerHeight === b.topClefHeight.innerHeight &&
    a.topClefHeight.outerHeight === b.topClefHeight.outerHeight &&
    a.bottomClefHeight.innerHeight === b.bottomClefHeight.innerHeight &&
    a.bottomClefHeight.outerHeight === b.bottomClefHeight.outerHeight &&
    a.topClefNumExtraLinesTop === b.topClefNumExtraLinesTop &&
    a.topClefNumExtraLinesBottom === b.topClefNumExtraLinesBottom &&
    a.bottomClefNumExtraLinesTop === b.bottomClefNumExtraLinesTop &&
    a.bottomClefNumExtraLinesBottom === b.bottomClefNumExtraLinesBottom
  );
}

function phraseGraphicResizeParamsEqual(
  prev: PhraseGraphicResizeParams | undefined,
  next: PhraseGraphicResizeParams,
): boolean {
  if (prev === undefined) return false;
  return (
    prev.width === next.width &&
    prev.x === next.x &&
    prev.y === next.y &&
    prev.phraseHeightInner === next.phraseHeightInner &&
    configValuesEqual(prev.newConfig, next.newConfig) &&
    phraseDimensValuesEqual(prev.phraseDimens, next.phraseDimens)
  );
}

export type StaffTransition = 'Draw' | 'Fade' | 'None';

export interface BoostChevronOptions {
  minWidth: number;
  maxWidth: number;
  minAspectRatio: number;
  maxAspectRatio: number;
  /** Thickness of the chevron strip (inset from outer edge). Pixels. */
  thickness: number;
  gap: number;
}

class AdaptiveRegionGraphic extends Phaser.GameObjects.Container {
  regionType: 'speed-up' | 'slow-down';
  chevrons: Phaser.GameObjects.Polygon[] = [];
  background: Phaser.GameObjects.Rectangle;
  tweenHandles: Phaser.Tweens.Tween[] = [];
  maskRect: Phaser.GameObjects.Rectangle;
  width: number;
  height: number;
  parentX: number;
  parentY: number;
  private gapTopY: number;
  private gapBottomY: number;
  private chevronOptions: BoostChevronOptions;

  constructor(
    scene: Scene,
    x: number,
    y: number,
    parentX: number,
    parentY: number,
    width: number,
    height: number,
    regionType: 'speed-up' | 'slow-down',
    gapTopY: number,
    gapBottomY: number,
    chevronOptions: BoostChevronOptions,
    skipAnimation?: boolean,
  ) {
    super(scene, x, y);
    this.parentX = parentX;
    this.parentY = parentY;
    this.regionType = regionType;
    this.width = width;
    this.height = height;
    this.gapTopY = gapTopY;
    this.gapBottomY = gapBottomY;
    this.chevronOptions = chevronOptions;
    const baseColor = regionType === 'speed-up' ? 0xeafafd : 0xfdf1ea;
    this.background = scene.add.rectangle(0, 0, this.width, this.height, baseColor, 1);
    this.background.setOrigin(0, 0);
    this.background.setAlpha(0);
    this.maskRect = scene.add.rectangle(0, 0, this.width, this.height, 0xffffff, 1);
    this.maskRect.setOrigin(0, 0);
    this.maskRect.setAlpha(0);
    this.add(this.maskRect);
    this.add(this.background);
    //this.setMask(this.background.createGeometryMask());
    this.createChevrons(skipAnimation);
  }

  createChevrons(skipAnimation?: boolean) {
    console.debug('[AdaptiveRegionGraphic] createChevrons');
    const regionWidth = this.width;
    const gapHeight = this.gapBottomY - this.gapTopY;
    if (gapHeight <= 0 || regionWidth <= 0) return;

    const opts = this.chevronOptions;
    const effectiveMinW = Math.max(opts.minWidth, opts.minAspectRatio * gapHeight);
    const effectiveMaxW = Math.min(opts.maxWidth, opts.maxAspectRatio * gapHeight);
    const t = opts.thickness;
    const interval = t + opts.gap;
    const w = (effectiveMinW + effectiveMaxW) / 2;
    const n = Math.max(1, Math.round(regionWidth / interval)) + 1;
    const step = interval;
    const h = gapHeight;
    const localGapTopY = this.gapTopY - this.y;

    this.background.setSize(regionWidth, this.height);
    this.background.setOrigin(0.5, 0.5);
    this.background.setPosition(regionWidth / 2, this.height / 2);
    this.setMask(this.maskRect.createGeometryMask());
    this.maskRect.setPosition(this.x + this.parentX, this.y + this.parentY);

    if (this.regionType === 'slow-down') {
      const colorChevron = 0xfee2cc;
      const chevronFillAlpha = 0.8;
      const delayStep = 500 / n;
      for (let i = 0; i < n; i++) {
        const cx = (i - 1) * step + step / 2 + interval / 2;
        const left = cx - w / 2;
        const top = localGapTopY;
        const points: number[] = t > 0 ? [w - t, 0, w, 0, t, h, 0, h] : [];
        const poly = this.scene.add.polygon(left + w, top, points, colorChevron, chevronFillAlpha);
        poly.setOrigin(0, 0);
        this.add(poly);
        poly.setAlpha(0);
        this.chevrons.push(poly);
        this.tweenHandles.push(
          this.scene.tweens.add({
            targets: poly,
            x: left,
            duration: skipAnimation ? 0 : 500,
            ease: 'Power3',
            delay: skipAnimation ? 0 : (n - 1 - i) * delayStep,
          }),
          this.scene.tweens.add({
            targets: poly,
            alpha: 1,
            duration: skipAnimation ? 0 : 500,
            ease: 'Power2',
            repeat: skipAnimation ? 0 : 3,
            delay: skipAnimation ? 0 : (n - 1 - i) * delayStep,
          }),
        );
      }
    } else {
      const chevronFillColor = 0xcceffe;
      const chevronFillAlpha = 0.8;
      const maxThickness = Math.min(w / 2, h / 2) * 0.99;
      const thickness = Math.max(0, Math.min(opts.thickness, maxThickness));
      for (let i = 0; i < n; i++) {
        const cx = (i - 1) * step + step / 2 + interval / 2;
        const left = cx - w / 2;
        const top = localGapTopY;
        const points: number[] =
          thickness > 0
            ? [0, 0, thickness, 0, w, h / 2, thickness, h, 0, h, w - thickness, h / 2]
            : [0, 0, 0, h, w, h / 2];
        const poly = this.scene.add.polygon(
          left - w,
          top,
          points,
          chevronFillColor,
          chevronFillAlpha,
        );
        poly.setOrigin(0, 0);
        this.add(poly);
        poly.setAlpha(0);
        this.chevrons.push(poly);
        this.tweenHandles.push(
          this.scene.tweens.add({
            targets: poly,
            x: left,
            duration: skipAnimation ? 0 : 500,
            ease: 'Power3',
            delay: skipAnimation ? 0 : i * 50,
          }),
          this.scene.tweens.add({
            targets: poly,
            alpha: 1,
            duration: skipAnimation ? 0 : 500,
            ease: 'Power2',
            repeat: skipAnimation ? 0 : 3,
            delay: skipAnimation ? 0 : i * 50,
          }),
        );
      }
    }
  }

  startAnimations(skipAnimation?: boolean) {
    this.tweenHandles.push(
      this.scene.tweens.add({
        targets: this.background,
        width: this.background.width,
        alpha: 1,
        duration: skipAnimation ? 0 : 1000,
        ease: 'Power2',
      }),
    );
  }

  destroy() {
    this.tweenHandles.forEach((t) => t.remove());
    this.tweenHandles.length = 0;
    this.chevrons.forEach((c) => c.destroy());
    this.chevrons.length = 0;
    super.destroy();
  }
}

export class PhraseData implements BaseData {
  bassClef: ClefData;
  trebleClef: ClefData;
  measureData: MeasureProps[] = [];
  timeSignature: TimeSignature;
  phraseIdx: number;
  serializedIterator: PrerenderedPhraseData[];
  startTimestamp: number;
  uuid: string;
  showBrace?: boolean;
  showClef?: boolean;
  endMeasure?: boolean;
  showTimeSig: boolean;

  constructor(
    phraseIdx: number,
    cursorData: CursorData,
    uuid: string,
    timeSignature: TimeSignature,
    showTimeSig: boolean,
    showBrace?: boolean,
    endMeasure?: boolean,
    showClef?: boolean,
    startTimestamp?: number,
  ) {
    this.uuid = uuid;
    this.startTimestamp = startTimestamp ?? 0;
    this.timeSignature = timeSignature;
    this.showTimeSig = showTimeSig;
    this.phraseIdx = phraseIdx;
    this.serializedIterator = cursorData.serializedIterator;
    this.showBrace = showBrace;
    this.showClef = showClef;
    this.endMeasure = endMeasure;
    let totalWidth = 0;
    const noteAdjustmentValues: number[] = [];
    this.measureData = cursorData.measureRects.map((rect, indx) => {
      const left = totalWidth;
      const notes = cursorData.serializedIterator
        .filter(({ currentMeasureIndex }) => currentMeasureIndex === indx)
        .flatMap((slice) =>
          slice.currentVoiceEntries.flatMap(({ Notes }) => Notes.flatMap((note) => note.graphics)),
        )
        .sort((a, b) => a.staveNote.left! - b.staveNote.left!);
      const firstNote = notes.at(0)!;
      const firstNoteRelativeOffset = firstNote.staveNote.left! - rect.x;
      const width = rect.width - firstNoteRelativeOffset;
      totalWidth += width;
      noteAdjustmentValues[indx] = firstNote.staveNote.left! - left;
      return {
        left,
        width,
        firstNoteRelativeOffset,
      };
    });
    // Since the notes in the serialized iterator are the actual x positions of the
    // elements in the source svg and therefore not adjusted to account for any gaps,
    // we remove the gaps and then transform and add them to the appropriate clef.
    const trebleNotes: VoiceData[] = [];
    const bassNotes: VoiceData[] = [];
    cursorData.serializedIterator.forEach(
      ({ currentTimestamp, currentVoiceEntries, currentMeasureIndex }) => {
        currentVoiceEntries.forEach(({ Notes }) =>
          Notes.forEach(
            ({ fingering, isRest, graphics, pitch, staffPosition, length, printObject }) => {
              if (fingering) console.debug('i_found_fingering');
              if (!printObject) return;
              const x =
                graphics[0].staveNote.left! -
                (noteAdjustmentValues[currentMeasureIndex] +
                  this.measureData[currentMeasureIndex].left);
              const voice = {
                fingering,
                x,
                id: graphics[0].staveNote.id,
                timestamp: currentTimestamp,
                voice: isRest ? 'rest' : { note: noteFromOsmdPitch(pitch) },
                length,
                measureIdx: currentMeasureIndex,
                staffPosition,
              } as VoiceData;
              if (staffPosition === 'top') {
                trebleNotes.push(voice);
              } else {
                bassNotes.push(voice);
              }
            },
          ),
        );
      },
    );
    this.trebleClef = {
      notes: trebleNotes,
      clef: 'treble',
      octaveOffset: 0,
      startTimestamp: startTimestamp ?? 0,
    };
    this.bassClef = {
      notes: bassNotes,
      clef: 'bass',
      octaveOffset: 0,
      startTimestamp: startTimestamp ?? 0,
    };
    const allNoteIds = [...trebleNotes, ...bassNotes].map((n) => n.id);
    console.log(
      `[PhraseData] uuid=${uuid} startTs=${startTimestamp} phraseIdx=${phraseIdx} trebleNotes=${
        trebleNotes.length
      } bassNotes=${bassNotes.length} vfIds=[${allNoteIds.join(',')}]`,
    );
  }
}

export function DefaultConfig(config: Partial<Config>): Config {
  const def = {
    globalScaling: config.globalScaling ?? 1,
    noteScaling: config.noteScaling ?? 1,
    pitchLineWidth: config.pitchLineWidth ?? 2,
    pitchLineGap: config.pitchLineGap ?? 20,
    measureLineWidth: config.measureLineWidth ?? 3,
    clefGap: config.clefGap ?? 35,
    firstNoteGap: config.firstNoteGap ?? 60,
    clefSymbolMargin: config.clefSymbolMargin ?? 20,
    centreAlign: config.centreAlign ?? false,
    notePaddingFactor: config.notePaddingFactor ?? 1,
    doubleEndLines: config.doubleEndLines ?? false,
  };
  return def;
}

export function MediumConfig(): Config {
  return {
    globalScaling: 1,
    noteScaling: 1,
    pitchLineWidth: 1.5,
    pitchLineGap: 15,
    measureLineWidth: 2,
    clefGap: 15,
    firstNoteGap: 60,
    clefSymbolMargin: 20,
    centreAlign: false,
    notePaddingFactor: 1,
    doubleEndLines: false,
  };
}

export function MinimumConfig(): Config {
  return {
    globalScaling: 1,
    noteScaling: 1,
    pitchLineWidth: 1,
    pitchLineGap: 10,
    measureLineWidth: 1.5,
    clefGap: 5,
    firstNoteGap: 30,
    clefSymbolMargin: 10,
    centreAlign: false,
    notePaddingFactor: 1,
    doubleEndLines: false,
  };
}

export function ScaledConfig(config: Config, scale: number): Config {
  return {
    globalScaling: config.globalScaling * scale,
    noteScaling: config.noteScaling * scale,
    pitchLineGap: config.pitchLineGap * scale,
    pitchLineWidth: config.pitchLineWidth * scale,
    measureLineWidth: config.measureLineWidth * scale,
    clefGap: config.clefGap * scale,
    firstNoteGap: config.firstNoteGap * scale,
    clefSymbolMargin: config.clefSymbolMargin * scale,
    centreAlign: config.centreAlign,
    notePaddingFactor: config.notePaddingFactor * scale,
    doubleEndLines: config.doubleEndLines,
  };
}

export class PhraseGraphicBase<DataType extends BaseData> extends GameObjects.Container {
  bassClef?: Clef;
  trebleClef?: Clef;
  config: Config;
  measureDataExtra: MeasureDataExtra[] = [];
  widthRaw?: number;
  _data: DataType;

  notes: Map<string, VoiceGraphic> = new Map();

  measureLines: GameObjects.Rectangle[] = [];
  brace?: Phaser.GameObjects.Image;
  totalWidth!: number;

  tweenHandles: Phaser.Tweens.Tween[] = [];

  phraseStartingMeasure: number;

  bottom?: boolean;
  _height: number = 0;

  clefGapGizmo?: Phaser.GameObjects.Rectangle;
  phraseDimens?: PhraseDimens;
  debugRectangle?: Phaser.GameObjects.Rectangle;

  alpha: number = 1;

  /** DPR used for rendering. Defaults to window.devicePixelRatio (lesson mode).
   *  Set to 1 for tutorial/exercise scenes that use a fixed viewport.
   *  Setting this value propagates it to child Clef and Note/Rest components. */
  private _renderDpr: number = window.devicePixelRatio;
  originMarker: any;
  oldResizeParams?: PhraseGraphicResizeParams;

  get renderDpr(): number {
    return this._renderDpr;
  }

  set renderDpr(value: number) {
    this._renderDpr = value;
    if (this.trebleClef) this.trebleClef.renderDpr = value;
    if (this.bassClef) this.bassClef.renderDpr = value;
  }

  adaptiveTempoRegion?: AdaptiveRegionGraphic;

  adaptiveSegmentState?: {
    startMeasure: number;
    endMeasure: number;
    direction: 'speed-up' | 'slow-down';
    hasEnded: boolean;
  };

  boostTweenHandles: Phaser.Tweens.Tween[] = [];

  /** Chevron layout: min/max width, aspect ratio, and strip thickness for the boosted region gap. */
  getDefaultBoostChevronOptions(): BoostChevronOptions {
    return {
      minWidth: 40 * this.renderDpr,
      maxWidth: 80 * this.renderDpr,
      minAspectRatio: 1,
      maxAspectRatio: 4,
      thickness: 30 * this.renderDpr,
      gap: 20 * this.renderDpr,
    };
  }

  constructor(
    scene: Scene,
    x: number,
    config: Partial<Config>,
    data: DataType,
    transition: StaffTransition,
    phraseStartingMeasure: number,
    startingTimestamp?: number,
    bottom?: boolean,
  ) {
    super(scene);
    this._data = data;
    this.bottom = bottom;
    this.phraseStartingMeasure = phraseStartingMeasure;
    this.scene = scene;
    this.config = DefaultConfig(config);
    this.notes = new Map();
    console.log(
      `[PhraseGraphic::constructor] bottom=${bottom} showClef=${data.showClef} showBrace=${data.showBrace} transition=${transition} trebleStartTs=${data.trebleClef.startTimestamp}`,
    );
    this.reset(data, x);
    console.log(
      `[PhraseGraphic::constructor] after reset: trebleClef=${!!this.trebleClef} bassClef=${!!this
        .bassClef} trebleClefSymbol=${!!this.trebleClef?.clefSymbol} bassClefSymbol=${!!this
        .bassClef?.clefSymbol} measureLines=${this.measureLines.length}`,
    );
    this.transition(transition);
    if (startingTimestamp) {
      const timestamp = this.scene.add.text(0, -20, startingTimestamp.toString(), {
        color: '#000000',
      });
      this.add(timestamp);
    }
    this.scene.add.existing(this);
  }

  shakeFirstNote() {
    const firstNote = this.getFirstNoteId();
    if (firstNote) {
      this.notes.get(firstNote)?.shake();
    }
  }

  highlightMeasure(measure: number) {
    this.bassClef?.highlightMeasure(measure);
    this.trebleClef?.highlightMeasure(measure);
  }

  getPhraseDimens() {
    return this.phraseDimens;
  }

  getBoundingBoxHeight() {
    return (this.phraseDimens?.bbHeight ?? 0) * this.renderDpr;
  }

  getInnerHeight() {
    return (this.phraseDimens?.innerHeight ?? 0) * this.renderDpr;
  }

  resize(
    newConfig: Config,
    width: number,
    x: number,
    y: number,
    phraseDimens: PhraseDimens,
    phraseHeightInner: number,
  ) {
    const nextResizeParams: PhraseGraphicResizeParams = {
      newConfig,
      width,
      x,
      y,
      phraseDimens,
      phraseHeightInner,
    };
    if (phraseGraphicResizeParamsEqual(this.oldResizeParams, nextResizeParams)) {
      console.debug('[PhraseOptimizer::resize] SKIPPED — same params');
      return;
    }
    this.oldResizeParams = nextResizeParams;

    if (!this.originMarker) {
      this.originMarker = this.scene.add.rectangle(0, 0, 40, 40, 0x00ff00, 0.1);
      this.originMarker.setOrigin(0, 0);
    }
    this.originMarker.setPosition(0, 0);
    console.debug('[PhraseOptimizer::resize]', { newConfig, phraseDimens, phraseHeightInner });
    if (this.isDestroyed) {
      console.warn(
        `[PhraseGraphic::resize] SKIPPED — phrase is destroyed startTs=${this._data.trebleClef.startTimestamp}`,
      );
      return;
    }
    console.log(
      `[PhraseGraphic::resize] startTs=${this._data.trebleClef.startTimestamp} bottom=${
        this.bottom
      } width=${width} x=${x} y=${y} trebleClef=${!!this.trebleClef} bassClef=${!!this
        .bassClef} trebleClefSymbol=${!!this.trebleClef?.clefSymbol} bassClefSymbol=${!!this
        .bassClef?.clefSymbol}`,
    );
    this.phraseDimens = phraseDimens;
    const {
      topClefHeight,
      bottomClefHeight,
      topClefNumExtraLinesTop,
      topClefNumExtraLinesBottom,
      bottomClefNumExtraLinesTop,
      bottomClefNumExtraLinesBottom,
    } = phraseDimens;
    const trebleClefHeightInner = topClefHeight.innerHeight;
    const trebleClefHeightOuter = topClefHeight.outerHeight;
    const bassClefHeightInner = bottomClefHeight.innerHeight;
    const bassClefHeightOuter = bottomClefHeight.outerHeight;
    const xScaled = x * this.renderDpr;
    const yScaled = y * this.renderDpr;
    this.config = newConfig;
    this.totalWidth = width * this.renderDpr;
    let xGap = 0;
    let { clefGap } = newConfig;
    clefGap *= this.renderDpr;
    clefGap += 1 * newConfig.pitchLineGap * this.renderDpr;
    console.debug('[StaffLine::resize] clefGap', clefGap);
    // rescale the brace
    this.brace?.setScale((phraseHeightInner * this.renderDpr) / this.brace.height);
    this.brace?.setY((phraseHeightInner * this.renderDpr) / 2);
    this.brace?.setX(0 - this.brace.displayWidth * 1.05);
    const braceWidth = this.brace?.displayWidth ?? 0;
    const braceGap = 10;
    xGap += braceWidth + (braceWidth ? braceGap : 0);
    // rescale the clef symbols and timesignature
    if ((this.trebleClef && !this.bassClef) || (!this.trebleClef && this.bassClef))
      console.error('having a single clef on the staff line is not yet supported!');
    if (
      this.trebleClef &&
      this.bassClef &&
      this.trebleClef.clefSymbol &&
      this.bassClef.clefSymbol
    ) {
      xGap += this.calculateClefSymbolSize(this.trebleClef.clefSymbol, this.bassClef.clefSymbol);
      //xGap += this.config.clefSymbolMargin;
    }
    if (
      (this.trebleClef?.timeSignatureGraphics?.length ?? 0) > 0 ||
      (this.bassClef?.timeSignatureGraphics?.length ?? 0) > 0
    ) {
      const num = [];
      const den = [];
      if (this.trebleClef) {
        num.push(this.trebleClef.timeSignatureGraphics[0]);
        den.push(this.trebleClef.timeSignatureGraphics[1]);
      }
      if (this.bassClef) {
        num.push(this.bassClef.timeSignatureGraphics[0]);
        den.push(this.bassClef.timeSignatureGraphics[1]);
      }
      xGap += this.calculateTimeSigTextSize(num, den) + this.config.clefSymbolMargin;
    }
    this.trebleClef?.newResize(
      width * this.renderDpr,
      trebleClefHeightInner,
      trebleClefHeightOuter,
      this.config,
      0,
      xGap,
      topClefNumExtraLinesTop,
      topClefNumExtraLinesBottom,
      0,
    );
    this.bassClef?.newResize(
      width * this.renderDpr,
      bassClefHeightInner,
      bassClefHeightOuter,
      this.config,
      0,
      xGap,
      bottomClefNumExtraLinesTop,
      bottomClefNumExtraLinesBottom,
      trebleClefHeightInner * this.renderDpr + clefGap,
    );
    this._height =
      trebleClefHeightInner * this.renderDpr +
      (this.bassClef && this.trebleClef ? bassClefHeightInner * this.renderDpr + clefGap : 0);
    const measures = this.trebleClef?.getMeasures() || this.bassClef?.getMeasures();
    const measureXOffset = this.trebleClef?.x || this.bassClef?.x;
    const clefWidth = this.trebleClef?.getWidth() || this.bassClef?.getWidth();
    console.debug('[StaffLine::resize] phraseHeightInner=', phraseHeightInner);
    if (measures && measureXOffset !== undefined && clefWidth) {
      this.measureLines[0].setX(measureXOffset);
      this.measureLines.forEach((l, idx) => {
        l.setSize(
          this.config.measureLineWidth *
            this.renderDpr *
            (this.config.doubleEndLines && idx == this.measureLines.length - 1 ? 3 : 1),
          (phraseHeightInner + this.config.pitchLineWidth) * this.renderDpr,
        );
        if (idx == 0 || idx >= measures.length) return;
        l.setX(measures[idx].x + measureXOffset - measures[idx].getOffset());
      });
      if (this.config.doubleEndLines) {
        this.measureLines[this.measureLines.length - 2].setX(clefWidth + measureXOffset - 10);
      }
      this.measureLines[this.measureLines.length - 1].setX(clefWidth + measureXOffset);
    }
    console.debug('[PhraseOptimizer::resize] xScaled', xScaled, 'yScaled', yScaled);
    this.adjustFingerNumbers();
    this.setX(xScaled);
    this.setY(yScaled);
    this.drawGizmos();
    if (this.adaptiveSegmentState && !this.adaptiveSegmentState.hasEnded) {
      this.setAdaptiveSegment(
        this.adaptiveSegmentState.startMeasure,
        this.adaptiveSegmentState.endMeasure,
        this.adaptiveSegmentState.direction,
        undefined,
        true,
      );
    } else {
      this.adaptiveTempoRegion?.destroy();
    }
  }

  setAdaptiveSegment(
    measureStart: number,
    measureEnd: number,
    direction: 'speed-up' | 'slow-down',
    chevronOptions?: Partial<BoostChevronOptions>,
    skipAnimation?: boolean,
  ) {
    console.debug('creating adaptive region');
    const startX = this.measureLines[measureStart].x;
    const endX =
      measureEnd >= this.measureLines.length - 1
        ? this.measureLines.last().x
        : this.measureLines[measureEnd + 1].x;
    const startY = 0;
    const endY = this.getInnerHeight();
    const regionWidth = endX - startX;
    const innerHeight = endY - startY;
    this.adaptiveSegmentState = {
      startMeasure: measureStart,
      endMeasure: measureEnd,
      direction: direction,
      hasEnded: false,
    };
    console.debug(
      '[StaffLine::boostMeasures] measureStart=',
      measureStart,
      'measureEnd=',
      measureEnd,
    );

    this.adaptiveTempoRegion?.destroy();
    this.adaptiveTempoRegion = undefined;
    this.boostTweenHandles.forEach((tween) => tween.remove());
    this.boostTweenHandles.length = 0;

    let gapTopY = startY;
    let gapBottomY = startY;
    if (this.trebleClef && this.bassClef) {
      gapTopY = this.trebleClef.y + this.trebleClef.getHighestLineY();
      gapBottomY = this.bassClef.y + this.bassClef.getHighestLineY();
    }
    const options: BoostChevronOptions = {
      ...this.getDefaultBoostChevronOptions(),
      ...chevronOptions,
    };
    this.adaptiveTempoRegion = new AdaptiveRegionGraphic(
      this.scene,
      startX,
      startY,
      this.x,
      this.y,
      regionWidth,
      innerHeight,
      direction,
      gapTopY,
      gapBottomY,
      options,
      skipAnimation,
    );
    this.add(this.adaptiveTempoRegion);
    this.sendToBack(this.adaptiveTempoRegion);
    this.adaptiveTempoRegion.startAnimations(skipAnimation);
  }

  doneAdaptiveSegment() {
    this.boostTweenHandles.forEach((tween) => tween.remove());
    this.boostTweenHandles.length = 0;
    if (this.adaptiveTempoRegion) {
      const region = this.adaptiveTempoRegion;
      this.boostTweenHandles.push(
        this.scene.tweens.add({
          targets: [region.background, ...region.chevrons],
          alpha: 0,
          duration: 1000,
          ease: 'Power2',
          onComplete: () => {
            region.destroy();
            this.adaptiveTempoRegion = undefined;
          },
        }),
      );
    }
    if (this.adaptiveSegmentState) this.adaptiveSegmentState.hasEnded = true;
  }

  drawGizmos() {
    this.clefGapGizmo?.destroy();
    //this.clefGapGizmo = this.scene.add.rectangle(0, this.trebleClef?.getInnerHeight)
  }

  adjustFingerNumbers() {
    if (!this.trebleClef && !this.bassClef) return;
    const padding = this.config.pitchLineGap * 0.5; /** window.devicePixelRatio;*/
    let highestNotePos = (this.trebleClef?.getHighestLineY() ?? -Infinity) - padding;
    let highestNote: VoiceGraphic | undefined = undefined;
    let lowestNotePos = (this.bassClef?.getLowestLineY() ?? Infinity) + 10;
    let lowestNote: VoiceGraphic | undefined = undefined;
    this.bassClef?.notes.forEach((note) => {
      if (note.isRest) return;
      const { y2 } = (note as NoteGraphic).getClefRelativeBounds();
      if (y2 + padding > lowestNotePos) {
        lowestNotePos = y2 + padding;
        lowestNote = note;
      }
      this.notes.set(note.id, note);
    });
    this.trebleClef?.notes.forEach((note) => {
      if (note.isRest) return;
      const { y1 } = (note as NoteGraphic).getClefRelativeBounds();
      if (y1 - padding < highestNotePos) {
        highestNotePos = y1 - padding;
        highestNote = note;
      }
      this.notes.set(note.id, note);
    });
    const h = this.getBoundingBoxHeight() + 20;
    console.debug('[StaffLine::adjustFingerNumbers] height', h, this.y);
    if (!this.debugRectangle) {
      this.debugRectangle = this.scene.add.rectangle(
        0,
        0,
        this.totalWidth,
        this.getBoundingBoxHeight(),
        0x0000ff,
        0.5,
      );
      this.debugRectangle.setOrigin(0, 0);
      this.add(this.debugRectangle);
    }
    /*const highestLineY =
      ((this.phraseDimens?.innerHeight ?? 0) * window.devicePixelRatio -
        this.getBoundingBoxHeight()) /
      2;
    const lowestLineY = highestLineY + this.getBoundingBoxHeight();*/
    /*console.debug(
      "[StaffLine::adjustFingerNumbers] ",
      "innerHeight",
      this.phraseDimens?.innerHeight,
      "boundingBoxHeight",
      this.getBoundingBoxHeight(),
      "y",
      y,
    );*/
    //this.debugRectangle.setSize(this.totalWidth, this.getBoundingBoxHeight());
    //this.debugRectangle.setPosition(0, highestLineY);
    this.bassClef?.notes.forEach((note) =>
      note.showFingering(
        this.bassClef!,
        highestNotePos,
        lowestNotePos,
        highestNote as NoteGraphic,
        lowestNote as NoteGraphic,
        'bottom',
        this.config.pitchLineGap,
      ),
    );
    this.trebleClef?.notes.forEach((note) =>
      note.showFingering(
        this.trebleClef!,
        highestNotePos,
        lowestNotePos,
        highestNote as NoteGraphic,
        lowestNote as NoteGraphic,
        'top',
        this.config.pitchLineGap,
      ),
    );
  }

  getPhraseStartX() {
    return this.measureLines[0].x + this.x;
  }

  containsMeasure(measure: number): boolean {
    return (
      measure >= this.phraseStartingMeasure &&
      measure < this.phraseStartingMeasure + this.measureDataExtra.length
    );
  }

  checkAllNotes(): boolean {
    const bass = this.bassClef?.checkAllNotes();
    const treble = this.trebleClef?.checkAllNotes();
    if (bass === undefined || treble === undefined) {
      console.warn('some notes have not finished playing yet!');
    }
    return bass === true && treble === true;
  }

  addTween(tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig) {
    this.tweenHandles.push(this.scene.tweens.add(tween));
  }

  isDestroyed = false;

  destroy() {
    this.isDestroyed = true;
    this.boostTweenHandles.forEach((tween) => tween.remove());
    this.boostTweenHandles.length = 0;
    this.adaptiveTempoRegion?.destroy();
    this.adaptiveTempoRegion = undefined;
    this.measureLines.forEach((l) => l.destroy());
    this.measureLines.clear();
    this.bassClef?.destroy();
    this.bassClef = undefined;
    this.trebleClef?.destroy();
    this.trebleClef = undefined;
    this.notes.clear();
    this.tweenHandles.forEach((tween) => tween.remove());
    this.tweenHandles.clear();
    super.destroy();
  }

  getNextPosition() {
    return this.x + this.getWidth(true);
  }

  getWidth(skipBrace?: boolean) {
    return this.totalWidth + (skipBrace ? 0 : (this.brace?.width ?? 0));
  }

  getWidthWithClef() {
    return this.totalWidth + (this.brace?.width ?? 0);
  }

  getNoteById(id: string, timestamp?: number) {
    //return this.notes.get(id);
    return this.trebleClef?.getNote(id, timestamp) ?? this.bassClef?.getNote(id, timestamp);
  }

  getNotePosition(id: string, timestamp?: number, rest?: boolean) {
    const notePos =
      this.trebleClef?.getNotePosition(id, timestamp, rest) ??
      this.bassClef?.getNotePosition(id, timestamp, rest);
    if (notePos !== undefined) {
      const x = notePos.x + this.x;
      const y = notePos.y + this.y;
      return { x, y };
    }
    return undefined;
  }

  getFirstNotePositionInMeasure(measure: number) {
    const trebleNote = this.trebleClef
      ? addOptional(this.trebleClef.getFirstNoteInMeasure(measure), this.x)
      : undefined;
    const bassNote = this.bassClef
      ? addOptional(this.bassClef.getFirstNoteInMeasure(measure), this.x)
      : undefined;
    console.debug('got first note', trebleNote, bassNote);
    if (trebleNote && bassNote) return trebleNote < bassNote ? trebleNote : bassNote;
    else return trebleNote ?? bassNote;
  }

  getFirstNotePosition(measure?: number) {
    const treblePos = this.trebleClef?.getFirstNotePosition(measure ?? 0) ?? Infinity;
    const bassPos = this.bassClef?.getFirstNotePosition(measure ?? 0) ?? Infinity;
    return Math.min(treblePos, bassPos) + this.x;
  }

  getX() {
    return this.x;
  }

  transitionOut(onComplete?: () => void, ctx?: object) {
    this.addTween({
      //targets: [...this.measureLines, ...[this.brace]],
      targets: this,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onUpdate: (tween: any) => {
        //console.debug('[transitionOut] progress', tween.progress, this.alpha);
        this.measureLines.forEach((line) => line.setAlpha(this.alpha));
        this.brace?.setAlpha(this.alpha);
        this.bassClef?.setAlpha(this.alpha);
        this.trebleClef?.setAlpha(this.alpha);
      },
      onComplete,
      callbackScope: ctx,
    });
    /*this.bassClef?.transitionOut();
    this.trebleClef?.transitionOut();*/
  }

  transition(style: StaffTransition) {
    console.debug('transitioning staff line', style);
    switch (style) {
      case 'Draw': {
        const lineHeight = this.measureLines[0].height;
        const braceX = this.brace?.x;
        this.measureLines.forEach((line) => (line.height = 0));
        this.brace?.setX(this.brace.x - 50);
        this.brace?.setAlpha(0);
        this.measureLines.forEach((line, idx) =>
          this.addTween({
            targets: line,
            delay: idx * 50,
            alpha: 1,
            height: lineHeight,
            ease: 'Power2',
            duration: 300,
          }),
        );
        if (this.brace)
          this.addTween({
            targets: [this.brace],
            x: braceX,
            alpha: 1,
            delay: 50,
            duration: 450,
            ease: 'Power2',
          });
        this.bassClef?.transition(style);
        this.trebleClef?.transition(style);
        break;
      }
      case 'Fade':
        this.addTween({
          targets: [...this.measureLines, ...[this.brace]],
          alpha: 1,
          duration: 1000,
          ease: 'Power2',
        });
        this.bassClef?.transition(style);
        this.trebleClef?.transition(style);
        break;
      case 'None':
        this.measureLines.forEach((line) => line.setAlpha(1));
        this.brace?.setAlpha(1);
        this.bassClef?.transition(style);
        this.trebleClef?.transition(style);
        break;
    }
  }

  getFirstNoteId(): string | undefined {
    return this.trebleClef?.getFirstNoteId() ?? this.bassClef?.getFirstNoteId();
  }

  calculateClefSymbolSize(
    trebleClefSymbol: Phaser.GameObjects.Image,
    bassClefSymbol: Phaser.GameObjects.Image,
  ) {
    // The height of the clef symbol happens to be proportional
    // relative to the gap between the pitch lines. After some trial
    // and error, I have found the following ratio to work quite well
    // for the proportions. This might change if the actual asset file
    // for the clef symbols are modified so tread carefully.
    const clefGapRatio = 1 / 27;
    const clefScale = clefGapRatio * this.config.pitchLineGap * this.renderDpr;
    trebleClefSymbol.setScale(clefScale);
    trebleClefSymbol.setRotation(-0.05);
    bassClefSymbol.setScale(clefScale);
    // It is important to draw both clef's since we'll be taking the
    // widest of the two to compute the necessary gap dynamically.
    const clefSymbolWidth = Math.max(trebleClefSymbol.displayWidth, bassClefSymbol.displayWidth);
    return clefSymbolWidth;
  }

  calculateTimeSigTextSize(
    timeSigNumerText: Phaser.GameObjects.Image[],
    timeSigDenText: Phaser.GameObjects.Image[],
  ) {
    const sc1 = timeSigNumerText[0].height / (2 * this.config.pitchLineGap * this.renderDpr);
    const sc2 = timeSigDenText[0].height / (2 * this.config.pitchLineGap * this.renderDpr);
    timeSigNumerText.forEach((text) => text.setScale(1 / sc1));
    timeSigDenText.forEach((text) => text.setScale(1 / sc2));
    return Math.max(timeSigNumerText[0].displayWidth, timeSigDenText[0].displayWidth);
  }

  reset(data: DataType, xPos: number) {
    const { centreAlign } = this.config;
    const height = this.getInnerHeight() ?? 200;
    if (data.showBrace) {
      this.brace = this.scene.add.image(0, height / 2, 'brace');
      const braceScale = this.brace.displayHeight / height;
      this.brace.setScale(braceScale);
      this.brace.setOrigin(0, 0.5);
      this.brace.alpha = 0;
      this.add(this.brace);
    }
    this.notes = new Map();
    let trebleClefSymbol;
    let bassClefSymbol;
    let timeSig;
    // Compute the new widths of every measure by taking into account
    // the configured padding values and other symbols that might take
    // up any space
    let acc = 0;
    const xOffset = this.brace?.width ? this.brace.width / 2 : 0;
    let extraGap = 0;
    this.measureDataExtra = data.measureData.map(({ width }, idx) => {
      // the first measure will include the clef symbols and time
      // signature (optional)
      extraGap = 0;
      if (idx === 0 && data.showClef) {
        // The clef symbols are created here and then passed onto the Clef
        // object for proper organisation. They must be created here as the
        // overall positioning and size numbers depend on their size.
        // The x, y values for the position can be zero as they're not
        // important for computing the size. The placement is handled
        // by the parent Clef object instead.
        bassClefSymbol = this.scene.add.image(xOffset, 0, 'bass_clef');
        trebleClefSymbol = this.scene.add.image(xOffset, 0, 'treble_clef');
        const clefSymbolWidth = this.calculateClefSymbolSize(trebleClefSymbol, bassClefSymbol);
        trebleClefSymbol.setAlpha(0);
        bassClefSymbol.setAlpha(0);
        extraGap = clefSymbolWidth + this.config.clefSymbolMargin;
        // We have to repeat the process for the timeSignature symbols as well
        console.debug('time sig', data.timeSignature);
        if (data.timeSignature && data.showTimeSig) {
          const sig = data.timeSignature;
          const timeSigNumText = [0, 1].map((_) =>
            this.scene.add.image(0, 0, `time_sig_${sig.numerator}`),
          );
          const timeSigDenText = [0, 1].map((_) =>
            this.scene.add.image(0, 0, `time_sig_${sig.denominator}`),
          );
          const _w = this.calculateTimeSigTextSize(timeSigNumText, timeSigDenText);
          // The time signature numbers should have heights that equal twice the gap between two pitch lines
          timeSig = [
            [timeSigNumText[0], timeSigDenText[0]],
            [timeSigNumText[1], timeSigDenText[1]],
          ];
          //extraGap += w + this.config.clefSymbolMargin;
        }
      }
      const thisWidthFull = width + extraGap + this.config.firstNoteGap;
      acc += thisWidthFull;
      return {
        left: acc - thisWidthFull,
        width,
        firstNoteRelativeOffset: this.config.firstNoteGap,
        extraGap,
      };
    });
    const _fullWidth = data.measureData
      .map(({ width }) => width)
      .reduce((prev, val) => prev + val, 0);
    const _widthGapless = data.measureData.reduce((prev, m) => prev + m.width, 0);
    this.bassClef = new Clef(
      this.scene,
      this.config,
      data.bassClef,
      this.measureDataExtra,
      this.renderDpr,
      data.timeSignature,
      bassClefSymbol,
      timeSig ? timeSig[0] : undefined,
    );
    console.debug('data fingering', data);
    this.bassClef.setX(xOffset);
    this.trebleClef = new Clef(
      this.scene,
      this.config,
      data.trebleClef,
      this.measureDataExtra,
      this.renderDpr,
      data.timeSignature,
      trebleClefSymbol,
      timeSig ? timeSig[1] : undefined,
    );
    this.trebleClef.setX(xOffset);
    this.add(this.bassClef);
    this.add(this.trebleClef);
    const grandStaffHeight =
      this.config.pitchLineGap * 8 + this.config.pitchLineWidth * 2 + this.config.clefGap;
    const strokeSize = this.config.measureLineWidth;
    let totalWidth = 0;
    this.measureLines = this.measureDataExtra.map(
      ({ left, width, firstNoteRelativeOffset, extraGap }) => {
        totalWidth += width + firstNoteRelativeOffset + extraGap;
        return this.scene.add.rectangle(
          left + xOffset,
          0,
          strokeSize,
          grandStaffHeight - this.config.pitchLineWidth,
          0x0,
          1,
        );
      },
    );
    this.measureLines.push(
      this.scene.add.rectangle(
        totalWidth - (data.endMeasure ? 20 : this.config.doubleEndLines ? 10 : 0) + xOffset,
        0,
        strokeSize,
        grandStaffHeight - this.config.pitchLineWidth,
        0x0,
        1,
      ),
    );
    if (data.endMeasure && this.config.doubleEndLines) {
      this.measureLines.push(
        this.scene.add.rectangle(
          totalWidth + xOffset,
          0,
          strokeSize * (data.endMeasure ? 5 : 1),
          grandStaffHeight - this.config.pitchLineWidth,
          0x0,
          1,
        ),
      );
    }
    this.measureLines.forEach((line) => {
      line.setOrigin(0, 0);
      this.add(line);
      line.setAlpha(0);
    });
    this.totalWidth = totalWidth;

    if (centreAlign) {
      this.x = this.scene.cameras.main.width / 2 - this.totalWidth / 2;
      this.y = this.scene.cameras.main.height / 2 - this.getInnerHeight() / 2;
    } else {
      this.x = xPos;
    }
    console.debug(
      '[PhraseGraphic::reset] data',
      data,
      'clefs',
      trebleClefSymbol,
      bassClefSymbol,
      'brace',
      this.brace,
    );
    this.adjustFingerNumbers();
  }
}

export function getPhraseEnd(phraseData: PhraseData) {
  return (
    phraseData.startTimestamp +
    (phraseData.timeSignature.numerator / phraseData.timeSignature.denominator) *
      phraseData.measureData.length
  );
}

export class PhraseGraphic extends PhraseGraphicBase<PhraseData> {
  /**
   * Convert hex color string (e.g., "#469f6a") to hex number (e.g., 0x469f6a)
   */
  private hexStringToNumber(hexString: string): number {
    const hex = hexString.replace('#', '');
    return parseInt(hex, 16);
  }

  /**
   * Get colorblind-aware color for error feedback.
   * Uses the same color system as the existing note coloring.
   */
  private getErrorFeedbackColor(
    errorClass: ERROR_REC_CLASS,
    colorblindType: COLORBLIND_TYPE | null,
  ): string {
    return getColorByCbType(errorClass, colorblindType);
  }

  /**
   * Calculate staff line number from MIDI note number for a given clef.
   */
  private calculateLineFromMidiNote(midiNote: number, clef: 'treble' | 'bass'): number {
    const baseNote = midiNumberToBaseNote(midiNote % 12);
    const octave = Math.floor(midiNote / 12) - 1;
    const clefObj = clef === 'treble' ? this.trebleClef : this.bassClef;
    if (!clefObj) {
      console.error(`Clef ${clef} not found`);
      return 4; // Default to middle line
    }
    return getNoteLineNumber(clefObj._data.octaveOffset, clefObj._data.clef, [baseNote, octave]);
  }

  /**
   * Find which measure contains the given x position (in phrase local coordinates).
   */
  private findMeasureForX(phraseLocalX: number): {
    measure: ClefMeasure;
    measureIdx: number;
    clef: 'treble' | 'bass';
  } | null {
    // Try treble clef first
    if (this.trebleClef) {
      const trebleLocalX = phraseLocalX - this.trebleClef.x;
      for (let i = 0; i < this.trebleClef.getMeasures().length; i++) {
        const measure = this.trebleClef.getMeasures()[i];
        const measureLocalX = trebleLocalX - measure.x;
        if (measureLocalX >= 0 && measureLocalX <= measure.measureData.width) {
          return { measure, measureIdx: i, clef: 'treble' };
        }
      }
    }
    // Try bass clef
    if (this.bassClef) {
      const bassLocalX = phraseLocalX - this.bassClef.x;
      for (let i = 0; i < this.bassClef.getMeasures().length; i++) {
        const measure = this.bassClef.getMeasures()[i];
        const measureLocalX = bassLocalX - measure.x;
        if (measureLocalX >= 0 && measureLocalX <= measure.measureData.width) {
          return { measure, measureIdx: i, clef: 'bass' };
        }
      }
    }
    return null;
  }

  /**
   * Spawn a phantom note for error feedback.
   * @param cursorWorldX - Cursor x position in world coordinates
   * @param errorType - Type of error (EARLY_ONSET, LATE_ONSET, WRONG_NOTE)
   * @param originalNoteId - ID of the original note (for timing errors, to get y position)
   * @param playedMidiNote - MIDI note number that was played (for wrong notes)
   * @param noteLength - Length of the note (from original note or default)
   * @param colorblindType - Colorblind type for color selection
   * @returns The spawned NoteGraphic or null if spawning failed
   */
  spawnPhantomNote(
    cursorWorldX: number,
    errorType: MidiEventState,
    originalNoteId: string,
    playedMidiNote: number | null,
    noteLength: number,
    colorblindType: COLORBLIND_TYPE | null,
  ): NoteGraphic | null {
    //if (process.env.REACT_APP_NODE_ENV === 'prd' || process.env.REACT_APP_NODE_ENV === 'stg') return null;
    if (process.env.REACT_APP_FEATURE_ERROR_FEEDBACK !== 'true') return null;
    // Transform cursor position from world to phrase local
    const phraseLocalX = cursorWorldX - this.x;

    // Find which measure contains the cursor
    const measureInfo = this.findMeasureForX(phraseLocalX);
    if (!measureInfo) {
      console.warn('Could not find measure for cursor position', cursorWorldX);
      return null;
    }

    const { measure, measureIdx, clef } = measureInfo;

    // Transform to measure local coordinates
    const clefLocalX = phraseLocalX - (clef === 'treble' ? this.trebleClef!.x : this.bassClef!.x);
    const measureLocalX = clefLocalX - measure.x;

    // Calculate y position
    let line: number;
    let _yPos: number;

    if (errorType === MidiEventState.WRONG_NOTE && playedMidiNote !== null) {
      // For wrong notes, calculate from played MIDI pitch
      line = this.calculateLineFromMidiNote(playedMidiNote, clef);
      _yPos = (line / 2) * this.config.pitchLineGap;
    } else {
      // For timing errors, use original note's position
      const originalNote = this.getNoteById(originalNoteId);
      if (!originalNote) {
        console.warn('Could not find original note', originalNoteId);
        return null;
      }
      // Get the note's line number - notes store their line number
      line = (originalNote as NoteGraphic).line;
      _yPos = (line / 2) * this.config.pitchLineGap;
    }

    // Determine color based on error type
    const errorClass =
      errorType === MidiEventState.WRONG_NOTE ? ERROR_REC_CLASS.ERROR : ERROR_REC_CLASS.SEMI_ERROR;
    const colorHex = getColorByCbType(errorClass, colorblindType);

    // Create unique ID for phantom note
    const phantomId = `phantom_${originalNoteId}_${Date.now()}`;

    // Create the phantom note
    const phantomNote = new NoteGraphic(
      this.scene,
      measureIdx,
      phantomId,
      measureLocalX,
      measureLocalX, // unscaledX same as x for phantom notes
      line,
      noteLength,
      this.config,
      0, // timestamp not needed for phantom notes
      9999, // idx - use high number to avoid conflicts
      playedMidiNote ?? 60, // midiNote - use played note or default
      clef === 'treble' ? this.trebleClef! : this.bassClef!,
      undefined, // no fingering for phantom notes
    );

    // Apply styling: semi-transparent with error color
    // Use setColor which accepts hex strings and handles conversion properly
    phantomNote.setColor(colorHex);
    phantomNote.setAlpha(0.3);

    // Add to measure container
    measure.add(phantomNote);

    return phantomNote;
  }

  /**
   * Spawn a duration bar for offset error feedback.
   * @param startWorldX - Cursor x position when note was pressed (world coordinates)
   * @param endWorldX - Cursor x position when note was released (world coordinates)
   * @param noteY - Y position of the note (in clef local coordinates)
   * @param clef - Which clef this bar belongs to
   * @param colorblindType - Colorblind type for color selection
   * @returns The spawned Graphics object or null if spawning failed
   */
  spawnPhantomBar(
    startWorldX: number,
    endWorldX: number,
    noteY: number,
    clef: 'treble' | 'bass',
    colorblindType: COLORBLIND_TYPE | null,
  ): Phaser.GameObjects.Graphics | null {
    if (process.env.REACT_APP_FEATURE_ERROR_FEEDBACK !== 'true') return null;
    // Transform positions from world to phrase local
    const startPhraseLocalX = startWorldX - this.x;
    const endPhraseLocalX = endWorldX - this.x;

    // Transform to clef local
    const clefObj = clef === 'treble' ? this.trebleClef : this.bassClef;
    if (!clefObj) {
      console.warn(`Clef ${clef} not found`);
      return null;
    }

    const startClefLocalX = startPhraseLocalX - clefObj.x;
    const endClefLocalX = endPhraseLocalX - clefObj.x;

    // Find which measure contains the start position
    const measureInfo = this.findMeasureForX(startPhraseLocalX);
    if (!measureInfo) {
      console.warn('Could not find measure for bar start position', startWorldX);
      return null;
    }

    const { measure } = measureInfo;

    // Transform to measure local
    const startMeasureLocalX = startClefLocalX - measure.x;
    const endMeasureLocalX = endClefLocalX - measure.x;

    // Calculate bar dimensions
    const barWidth = Math.abs(endMeasureLocalX - startMeasureLocalX);
    const barHeight = this.config.pitchLineGap * 0.9; // 90% of note head height
    const barX = Math.min(startMeasureLocalX, endMeasureLocalX);
    const barY = noteY - barHeight / 2;

    // Get color
    const colorHex = getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, colorblindType);
    // Convert hex string to number for Phaser Graphics
    const colorNum = this.hexStringToNumber(colorHex);

    // Create graphics object
    const graphics = this.scene.add.graphics();
    graphics.setAlpha(0.3);
    graphics.fillStyle(colorNum, 1);
    graphics.fillRect(barX, barY, barWidth, barHeight);

    // Add vertical line marker at end
    const endMarkerX = endMeasureLocalX;
    graphics.lineStyle(2, colorNum, 1);
    graphics.lineBetween(endMarkerX, barY, endMarkerX, barY + barHeight);

    // Add to measure container
    measure.add(graphics);

    return graphics;
  }
}

export type VoiceGraphic = NoteGraphic | RestGraphic;
